import { toastWithUndo } from "@/components/ui/ToastSurface";
import type { Section, SectionEdit } from "@patent-studio/api-types";
import { childActions } from "./child-actions";
import {
  addDefinitions,
  addTextLine,
  deleteSection,
  deleteSubsection,
  putBack,
  renameSection,
  rewordLine,
} from "./framework-edits";
import type { SectionActions } from "./SectionCard";

/**
 * An edit made against the framework as the last write left it; null when it cannot be made.
 * `refused` is what the attorney is told then; `putBack` undoes what the page showed early; `control` names it.
 */
export type Send = (
  edit: (edits: SectionEdit[]) => SectionEdit[] | null,
  options?: { refused?: string; putBack?: () => void; control?: string },
) => void;

/** What the attorney is told when there is no section left to put a line back in. */
export const NOWHERE =
  "There is no section to put it back in. Add a section first.";

const MOVE_TARGET_GONE =
  "The section you chose to move them to is gone. Choose another.";

/** What each section's controls send: the one change, made against the latest framework. */
export function sectionActions(
  section: Section,
  send: Send,
  { offerDefinitions }: { offerDefinitions: boolean },
): SectionActions {
  const sectionId = section.section_id;
  return {
    addDefinitions: offerDefinitions
      ? () => {
          const id = crypto.randomUUID();
          send((edits) => addDefinitions(edits, sectionId, id), {
            control: "add definitions",
          });
        }
      : undefined,
    rename: (title, putBack) => {
      send((edits) => renameSection(edits, sectionId, title), {
        putBack,
        control: "section title",
      });
    },
    reword: (subsectionId, text, putBack) => {
      send((edits) => rewordLine(edits, subsectionId, text), {
        putBack,
        control: "line",
      });
    },
    ...childActions(send),
    deleteSubsection: (subsection) => {
      const deleted = {
        kind: "subsection" as const,
        subsection,
        from_section_id: sectionId,
        position: (section.subsections ?? []).indexOf(subsection),
      };
      send((edits) => deleteSubsection(edits, subsection.subsection_id), {
        control: "delete line",
      });
      toastWithUndo(
        subsection.origin === "attorney" ? "Deleted." : "Moved to Removed.",
        () => {
          send((edits) => putBack(edits, deleted), { refused: NOWHERE });
        },
      );
    },
    deleteSection: (moveTo) => {
      send((edits) => deleteSection(edits, sectionId, moveTo), {
        refused: MOVE_TARGET_GONE,
        control: "delete section",
      });
    },
    addLine: (text, putBack) => {
      const id = crypto.randomUUID();
      send((edits) => addTextLine(edits, sectionId, text, id), {
        putBack,
        control: "add line",
      });
    },
  };
}
